import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { activityApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ActivityCardSkeleton } from '../common/LoadingComponents';
import { ROUTES } from '../../utils/constants';
import ActivityCard from './ActivityCard';
import ActivityForm from './ActivityForm';

function Community() {
  const { isAuthenticated, user } = useAuth();
  const toast = useToast();
  
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [pagination, setPagination] = useState(null);

  const fetchActivities = useCallback(async (page = 1) => {
    setLoading(true);

    try {
      const params = { page, limit: 20 };
      if (filter !== 'all') {
        params.type = filter;
      }

      const response = await activityApi.getActivities(params);
      setActivities(response.activities || []);
      setPagination(response.pagination);
    } catch (err) {
      console.error('Failed to fetch activities:', err);
      toast.error('Unable to load community posts. Please try again.', {
        action: { label: 'Retry', onClick: () => fetchActivities(page) }
      });
    } finally {
      setLoading(false);
    }
  }, [filter, toast]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleActivityCreated = (newActivity) => {
    setActivities([newActivity, ...activities]);
    setShowForm(false);
    toast.success('Post created successfully!');
  };

  const handleActivityUpdated = (updatedActivity) => {
    setActivities(activities.map(activity => 
      activity._id === updatedActivity._id ? updatedActivity : activity
    ));
    setEditingActivity(null);
    toast.success('Post updated successfully!');
  };

  const handleActivityDeleted = (deletedId) => {
    setActivities(activities.filter(activity => activity._id !== deletedId));
  };

  const handleReaction = async (activityId, value) => {
    if (!isAuthenticated) return;

    try {
      const response = await activityApi.reactToActivity(activityId, value);
      
      // Update the activity in the list with new reaction data from server
      setActivities(activities.map(activity => {
        if (activity._id === activityId) {
          return {
            ...activity,
            reactions: response.reactions
          };
        }
        return activity;
      }));
    } catch (err) {
      console.error('Failed to react to activity:', err);
      toast.error('Unable to update your reaction. Please try again.');
    }
  };

  const handleActivityUpdate = (updatedActivity) => {
    setActivities(activities.map(activity => 
      activity._id === updatedActivity._id ? updatedActivity : activity
    ));
  };

  const filterButtons = [
    { key: 'all', label: 'All', count: null },
    { key: 'announcement', label: 'Announcements', count: null },
    { key: 'event', label: 'Events', count: null },
    { key: 'discussion', label: 'Discussions', count: null }
  ];

  if (loading && activities.length === 0) {
    return (
      <div className="community-page">
        <div className="community-header">
          <h1>Community</h1>
        </div>
        <div className="community-content">
          <ActivityCardSkeleton />
          <ActivityCardSkeleton />
          <ActivityCardSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="community-page">
      <div className="community-header">
        <div className="community-title">
          <h1>Community Board</h1>
          <p className="community-subtitle">Share announcements, events, and discussions with the community</p>
        </div>
        
        {isAuthenticated ? (
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary new-activity-btn"
          >
            New Post
          </button>
        ) : (
          <div className="auth-prompt-buttons">
            <Link to={ROUTES.LOGIN} className="btn btn-outline">
              Sign In
            </Link>
            <Link to={ROUTES.REGISTER} className="btn btn-primary">
              Sign Up to Post
            </Link>
          </div>
        )}
      </div>

      <div className="community-filters">
        {filterButtons.map(filterBtn => (
          <button
            key={filterBtn.key}
            onClick={() => setFilter(filterBtn.key)}
            className={`filter-chip ${filter === filterBtn.key ? 'active' : ''}`}
          >
            {filterBtn.label}
          </button>
        ))}
      </div>

      <div className="activity-feed">
        {!isAuthenticated && (
          <div className="auth-prompt-banner">
            <p>
              <strong>Join the community!</strong> Sign in or create an account to post, comment, and vote on activities.
            </p>
            <div className="auth-prompt-actions">
              <Link to={ROUTES.LOGIN} className="btn btn-outline">
                Sign In
              </Link>
              <Link to={ROUTES.REGISTER} className="btn btn-primary">
                Create Account
              </Link>
            </div>
          </div>
        )}
        
        {activities.length === 0 && !loading ? (
          <div className="empty-state">
            <h3>No activities yet</h3>
            <p>Be the first to share something with the community!</p>
            {isAuthenticated && (
              <button
                onClick={() => setShowForm(true)}
                className="btn btn-primary"
              >
                Create First Post
              </button>
            )}
          </div>
        ) : (
          activities.map(activity => (
            <ActivityCard
              key={activity._id}
              activity={activity}
              currentUser={user}
              isAuthenticated={isAuthenticated}
              onEdit={setEditingActivity}
              onDelete={handleActivityDeleted}
              onReaction={handleReaction}
              onUpdate={handleActivityUpdate}
            />
          ))
        )}
      </div>

      {pagination && pagination.total_pages > 1 && (
        <div className="pagination">
          <button
            onClick={() => fetchActivities(pagination.current_page - 1)}
            disabled={!pagination.has_prev}
            className="btn btn-secondary"
          >
            Previous
          </button>
          
          <span className="pagination-info">
            Page {pagination.current_page} of {pagination.total_pages}
          </span>
          
          <button
            onClick={() => fetchActivities(pagination.current_page + 1)}
            disabled={!pagination.has_next}
            className="btn btn-secondary"
          >
            Next
          </button>
        </div>
      )}

      {showForm && (
        <ActivityForm
          onSubmit={handleActivityCreated}
          onCancel={() => setShowForm(false)}
        />
      )}

      {editingActivity && (
        <ActivityForm
          activity={editingActivity}
          onSubmit={handleActivityUpdated}
          onCancel={() => setEditingActivity(null)}
        />
      )}
    </div>
  );
}

export default Community;